import { Link } from 'react-router-dom';

interface ServiceHeroProps {
    data: {
        title: string;
        subtitle: string;
        heroTitle?: string;
        breadcrumbLabel?: string;
        description: string;
        image: string;
        hideTitle?: boolean;
    }
}

export const ServiceHero = ({ data }: ServiceHeroProps) => {
    return (
        <div className="font-vanguard-display">
            {/* ── Desktop hero (lg+): texto esquerda, imagem direita absoluta ── */}
            <header className="relative h-[500px] items-center bg-white dark:bg-pacces-ebony hidden lg:flex">
                {/* Imagem direita */}
                <div className="absolute inset-y-0 right-0 w-1/2 z-0 overflow-hidden">
                    <img
                        src={data.image}
                        alt={data.subtitle}
                        className="w-full h-full object-cover grayscale-[0.3] hover:grayscale-0 transition-all duration-1000"
                        fetchPriority="high"
                    />
                    <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-white dark:from-pacces-ebony to-transparent"></div>
                </div>

                {/* Conteúdo */}
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="w-3/5">
                        <Breadcrumb subtitle={data.breadcrumbLabel ?? data.subtitle} />
                        {!data.hideTitle && <HeroTitle title={data.title} subtitle={data.subtitle} heroTitle={data.heroTitle} />}
                        <p className="text-lg text-stone-500 max-w-xl leading-relaxed font-light font-vanguard-sans">
                            {data.description}
                        </p>
                    </div>
                </div>
            </header>

            {/* ── Mobile/tablet hero (< lg): texto em cima, imagem em baixo ── */}
            <div className="lg:hidden bg-white dark:bg-pacces-ebony">
                {/* Texto */}
                <div className="px-4 sm:px-6 pt-10 pb-8">
                    <Breadcrumb subtitle={data.subtitle} />
                    {/* Visualmente idêntico ao HeroTitle do desktop, mas renderizado como div
                        para evitar H1 duplicado no DOM (o H1 semântico está no layout desktop acima) */}
                    {!data.hideTitle && <HeroTitleDisplay title={data.title} subtitle={data.subtitle} heroTitle={data.heroTitle} />}
                    <p className="text-base text-stone-500 leading-relaxed font-light font-vanguard-sans">
                        {data.description}
                    </p>
                </div>

                {/* Imagem em bloco abaixo do texto */}
                <div className="w-full h-56 sm:h-72 overflow-hidden">
                    <img
                        src={data.image}
                        alt={data.subtitle}
                        className="w-full h-full object-cover grayscale-[0.3]"
                        fetchPriority="high"
                    />
                </div>
            </div>
        </div>
    );
};

const Breadcrumb = ({ subtitle }: { subtitle: string }) => (
    <nav aria-label="Caminho de Navegação" className="flex mb-8 text-[12px] font-bold text-vanguard-primary uppercase tracking-[0.3em] font-vanguard-sans">
        <ol className="flex items-center flex-wrap gap-y-1 gap-x-2">
            <li><Link className="hover:text-stone-900 transition-colors" to="/">Início</Link></li>
            <li className="opacity-30">/</li>
            <li className="text-stone-400">Áreas de Atuação</li>
            <li className="opacity-30">/</li>
            <li className="text-stone-900 dark:text-white">{subtitle}</li>
        </ol>
    </nav>
);

const HeroTitle = ({ title, subtitle, heroTitle }: { title: string; subtitle: string; heroTitle?: string }) => (
    <>
        <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A0A0A] dark:text-white mb-6 uppercase tracking-tighter leading-[0.9] font-serif text-balance">
            {heroTitle ?? <>{title} <br /> {subtitle}</>}
        </h1>
        <div className="w-12 h-[2px] bg-vanguard-primary mb-6 lg:mb-10"></div>
    </>
);

const HeroTitleDisplay = ({ title, subtitle, heroTitle }: { title: string; subtitle: string; heroTitle?: string }) => (
    <div aria-hidden="true">
        <div className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A0A0A] dark:text-white mb-6 uppercase tracking-tighter leading-[0.9] font-serif text-balance">
            {heroTitle ?? <>{title} <br /> {subtitle}</>}
        </div>
        <div className="w-12 h-[2px] bg-vanguard-primary mb-6 lg:mb-10"></div>
    </div>
);
